import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Plus, Trash2, X, Loader2, Languages } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, F } from "@/pages/settings/AdminTabs";
import { shrinkImage } from "@/components/GuestForm";
import { api, API, errMsg } from "@/lib/api";

const TAGS = ["nome", "struttura", "arrivo", "partenza", "camera", "ospiti", "link_checkin"];
const LANGS = [["it", "Italiano"], ["en", "English"], ["de", "Deutsch"], ["fr", "Français"], ["es", "Español"]];
const EMPTY = { name: "", body: "", bodies: {}, arrival: false, photo_ids: [] };

function Editor({ value, onSaved, onCancel }) {
  const [f, setF] = useState({ ...value, bodies: value.bodies && Object.keys(value.bodies).length ? { ...value.bodies } : { it: value.body } });
  const [lang, setLang] = useState("it");
  const text = f.bodies[lang] || "";
  const setText = (v) => setF((x) => ({ ...x, bodies: { ...x.bodies, [lang]: v } }));
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const ta = useRef(null);
  const addTag = (tag) => {
    const el = ta.current;
    const pos = el && document.activeElement === el ? el.selectionStart : text.length;
    const ins = `{${tag}}`;
    setText(`${text.slice(0, pos)}${ins}${text.slice(pos)}`);
    requestAnimationFrame(() => {
      if (el) {
        el.focus();
        el.setSelectionRange(pos + ins.length, pos + ins.length);
      }
    });
  };
  const upload = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    setBusy(true);
    const ids = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", await shrinkImage(file, 1600));
        const { data } = await api.post("/templates/photos", fd);
        ids.push(data.id);
      } catch (err) {
        toast.error(errMsg(err));
      }
    }
    setF((x) => ({ ...x, photo_ids: [...x.photo_ids, ...ids] }));
    setBusy(false);
  };
  const [tr, setTr] = useState(false);
  const translate = async () => {
    const filled = ["en", "de", "fr", "es"].filter((c) => f.bodies[c]?.trim());
    if (filled.length && !window.confirm("Le traduzioni già scritte verranno sostituite. Continuare?")) return;
    setTr(true);
    try {
      const { data } = await api.post("/templates/translate", { text: f.bodies.it || "" });
      setF((x) => ({ ...x, bodies: { ...x.bodies, ...data.bodies } }));
      toast.success("Traduzioni compilate: controllale e salva");
      if (data.warnings?.length) toast.warning(`Controlla i segnaposto in: ${data.warnings.join(", ").toUpperCase()}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setTr(false);
  };
  const save = async () => {
    try {
      const payload = { ...f, body: f.bodies.it || "" };
      if (f.id) await api.put(`/templates/${f.id}`, payload);
      else await api.post("/templates", payload);
      toast.success("Messaggio salvato");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="rounded-xl border p-4 space-y-3 bg-stone-50" data-testid="template-editor">
      <F label="Nome del messaggio"><Input value={f.name} placeholder="Es. Istruzioni per l'arrivo" onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="template-name-input" /></F>
      <div className="flex flex-wrap gap-1.5" data-testid="template-lang-tabs">
        {LANGS.map(([c, l]) => (
          <button key={c} type="button" onClick={() => setLang(c)} className={`rounded-full border px-3 py-1 text-xs font-semibold ${lang === c ? "bg-primary text-white border-primary" : "bg-white"}`} data-testid={`template-lang-${c}`}>
            {l}{f.bodies[c]?.trim() ? " ✓" : ""}
          </button>
        ))}
        <Button type="button" size="sm" variant="outline" className="h-7 rounded-full text-xs" disabled={tr || !f.bodies.it?.trim()} onClick={translate} data-testid="template-translate">
          {tr ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Languages className="h-3.5 w-3.5 mr-1" />}{tr ? "Traduzione…" : "Traduci dall'italiano"}
        </Button>
      </div>
      <F label={`Testo (${LANGS.find(([c]) => c === lang)[1]})`}>
        <Textarea ref={ta} rows={8} value={text} onChange={(e) => setText(e.target.value)} placeholder={lang === "it" ? "Ciao {nome}! Ecco come raggiungere {struttura}…" : "Facoltativo: se vuoto si usa il testo in italiano"} data-testid="template-body-input" />
      </F>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.arrival} onChange={(e) => setF({ ...f, arrival: e.target.checked })} data-testid="template-arrival-checkbox" />Usa come <b>istruzioni d'arrivo</b> (promemoria in dashboard il giorno prima dell'arrivo)</label>
      <div className="flex flex-wrap gap-1.5">
        <span className="text-xs text-stone-500 mr-1 self-center">Inserisci:</span>
        {TAGS.map((t) => <button key={t} type="button" onClick={() => addTag(t)} className="rounded-full border bg-white px-2 py-0.5 text-xs hover:border-primary" data-testid={`template-tag-${t}`}>{`{${t}}`}</button>)}
      </div>
      <div className="flex flex-wrap gap-2">
        {f.photo_ids.map((p) => (
          <div key={p} className="relative">
            <img src={`${API}/public/media/${p}`} alt="" className="h-20 w-20 rounded-lg object-cover border" />
            <button type="button" onClick={() => setF({ ...f, photo_ids: f.photo_ids.filter((x) => x !== p) })} className="absolute -top-2 -right-2 rounded-full bg-white border p-0.5" aria-label="Rimuovi foto" data-testid={`template-photo-remove-${p}`}><X className="h-3 w-3" /></button>
          </div>
        ))}
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={upload} data-testid="template-photo-input" />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className="grid h-20 w-20 place-items-center rounded-lg border-2 border-dashed text-stone-500 hover:border-primary" data-testid="template-photo-add">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
        </button>
      </div>
      <p className="text-xs text-stone-500">WhatsApp non permette di allegare foto da un link: le foto vengono aggiunte in fondo al messaggio come link, che l'ospite apre con un tocco.</p>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onCancel} data-testid="template-cancel">Annulla</Button>
        <Button onClick={save} disabled={busy} data-testid="template-save">Salva</Button>
      </div>
    </div>
  );
}

export function TemplatesTab() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/templates").then(({ data }) => setList(data));
  useEffect(() => {
    load();
  }, []);
  const remove = async (t) => {
    if (!window.confirm(`Eliminare il messaggio "${t.name}"?`)) return;
    try {
      await api.delete(`/templates/${t.id}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Card title="Messaggi WhatsApp" desc="Testi pronti (es. istruzioni per l'arrivo) da inviare agli ospiti dall'elenco Prenotazioni.">
      <div className="space-y-3">
        {list.map((t) => (editing?.id === t.id ? (
          <Editor key={t.id} value={editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
        ) : (
          <div key={t.id} className="flex items-start justify-between gap-3 rounded-xl border p-3" data-testid={`template-row-${t.id}`}>
            <div className="min-w-0">
              <p className="font-semibold">{t.name}{t.arrival && <span className="ml-2 rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-800">Istruzioni d'arrivo</span>}</p>
              <p className="text-sm text-stone-500 line-clamp-2 whitespace-pre-line">{t.body}</p>
              <p className="text-xs text-stone-400 mt-1">Lingue: {(Object.keys(t.bodies || {}).length ? Object.keys(t.bodies) : ["it"]).join(" · ").toUpperCase()}{t.photo_ids.length > 0 ? ` · ${t.photo_ids.length} foto` : ""}</p>
            </div>
            <div className="flex gap-1 shrink-0">
              <Button size="sm" variant="outline" onClick={() => setEditing(t)} data-testid={`template-edit-${t.id}`}>Modifica</Button>
              <Button size="icon" variant="ghost" className="text-rose-700" onClick={() => remove(t)} aria-label="Elimina" data-testid={`template-delete-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        )))}
        {editing && !editing.id ? (
          <Editor value={editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
        ) : (
          <Button variant="outline" onClick={() => setEditing({ ...EMPTY })} data-testid="template-new"><Plus className="h-4 w-4 mr-1" />Nuovo messaggio</Button>
        )}
      </div>
    </Card>
  );
}
